import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { PageSection, PageSectionFormProps } from './PageSectionForm.d'
import { Card } from 'primereact/card'
import { api } from '../../../../auth'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Language } from '../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../common/forms/fields/TextField'
import SelectField from '../../../common/forms/fields/SelectField'
import ImageField from '../../../common/forms/fields/ImageField'
import RecordStatusField from '../../../common/forms/fields/RecordStatusField'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderTranslatedFields from '../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import FormActions from '../../../common/forms/FormActions'
import { getGroupTranslation } from '../../../common/utility/helpers'
import PageSectionFormContent from './PageSectionFormContent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import { HintMessage } from '../../../common/messages/Messages'
import ImageReccomendedDimentionsMessage from '../../../common/messages/ImageReccomendedDimentionsMessage'

const PageSectionForm: FC<PageSectionFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
    onClose,
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'PAGE_SECTION_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = api

    const emptyPageSection: PageSection = {
        page_sections_id: null as any,
        name: '',
        value: '',
        image_id: null,
        pillar_image: null,
        active: false,
        remove_pillar_image: false,
        cta_target: '',
        cta_label: '',
        cta_url: '',
        cta_page_sections_id: null,
        widget_data: null,
        use_data_type: false,
    }

    const [pageDialog, setPageDialog] = useState(true)
    const [pageSection, setPageSection] =
        useState<PageSection>(emptyPageSection)
    const [availablePages, setAvailablePages] = useState<[]>([])
    const [availableWidgets, setAvailableWidgets] = useState<[]>([])
    const [availableModules, setAvailableModules] = useState<[]>([])
    const [contentIsWidget, setContentIsWidget] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let _initialValues: Record<string, any> = {
        ...pageSection,
        value: pageCode + '_VALUE',
        cta_label: pageCode + '_CTA_LABEL',
        widget_data: {
            ...pageSection.widget_data,
            widget_header: pageCode + '_WIDGET_OPTIONAL_DATA_HEADER',
            widget_sub_header: pageCode + '_WIDGET_OPTIONAL_DATA_SUB_HEADER',
            widget_no_records_message:
                pageCode + '_WIDGET_OPTIONAL_DATA_NO_RECORDS_MESSAGE',
            widget_more_label: pageCode + '_WIDGET_OPTIONAL_DATA_MORE_LABEL',
        },
    }

    let schema: Record<string, any> = {
        page_sections_id: Yup.string()
            .trim()
            .required('Select a page (where this section will appear)'),
        name: Yup.string().trim().required('Enter Section Internal Name'),
        value: Yup.string().trim().required('Required'),
        cta_label: Yup.string()
            .trim()
            .when('cta_target', (cta_target, schema) => {
                if (
                    cta_target.includes('internal') ||
                    cta_target.includes('external')
                ) {
                    return schema.required('Required')
                } else {
                    return schema.notRequired()
                }
            }),
        cta_url: Yup.string()
            .trim()
            .when('cta_target', (cta_target, schema) => {
                if (cta_target.includes('external')) {
                    return schema.required('Enter an external url')
                } else {
                    return schema.notRequired()
                }
            }),
        cta_page_sections_id: Yup.string()
            .trim()
            .when('cta_target', (cta_target, schema) => {
                if (cta_target.includes('internal')) {
                    return schema.required('Select Target Page')
                } else {
                    return schema.notRequired()
                }
            }),
    }

    const [initialValues, setInitialValues] = useState(_initialValues);

    (Object.values(initData.languages.data) as Language[]).map((lang) => {
        schema[`cta_label_${lang.id}`] = Yup.string()
            .trim()
            .when('cta_target', (cta_target, schema) => {
                if (
                    cta_target.includes('internal') ||
                    cta_target.includes('external')
                ) {
                    return schema.required(
                        `The button label (${lang.name}) is required`
                    )
                } else {
                    return schema.notRequired()
                }
            })
        _initialValues[`cta_label_${lang.id}`] = ''
        return true
    })

    const PageSectionFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/page-components/sections/' + id
            : '/page-components/sections/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data.section) {
                    const isWidget =
                        typeof response.data.data.section.widget_data !==
                            'undefined' &&
                        response.data.data.section.widget_data !== null &&
                        typeof response.data.data.section.widget_data ===
                            'object' &&
                        Object.keys(response.data.data.section.widget_data)
                            .length > 0

                    setContentIsWidget(isWidget)

                    setPageSection(response.data.data.section)

                    setInitialValues((prevState) => {
                        return {
                            ...prevState,
                            ...response.data.data.section,
                        }
                    })

                    Object.keys(initData.languages.data).map((locale) => {
                        setPageSection((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations: Record<string, any> = getGroupTranslation(
                                response.data.data.translations,
                                [
                                    {
                                        key: 'value',
                                        code: response.data.data.section.value,
                                    },
                                    {
                                        key: 'cta_label',
                                        code: response.data.data.section
                                            .cta_label,
                                    },
                                    {
                                        key: 'widget_header',
                                        code: isWidget
                                            ? response.data.data.section
                                                  .widget_data.widget_header
                                            : '',
                                    },
                                    {
                                        key: 'widget_sub_header',
                                        code: isWidget
                                            ? response.data.data.section
                                                  .widget_data.widget_sub_header
                                            : '',
                                    },
                                    {
                                        key: 'widget_no_records_message',
                                        code: isWidget
                                            ? response.data.data.section
                                                  .widget_data
                                                  .widget_no_records_message
                                            : '',
                                    },
                                    {
                                        key: 'widget_more_label',
                                        code: isWidget
                                            ? response.data.data.section
                                                  .widget_data.widget_more_label
                                            : '',
                                    },
                                ],
                                locale
                            )

                            setInitialValues((prevState) => {
                                return {
                                    ...prevState,
                                    [`value_${lang.id}`]:
                                        groupTranslations['value'],
                                    [`cta_label_${lang.id}`]:
                                        groupTranslations['cta_label'],
                                    [`widget_header_${lang.id}`]:
                                        groupTranslations['widget_header'],
                                    [`widget_sub_header_${lang.id}`]:
                                        groupTranslations['widget_sub_header'],
                                    [`widget_no_records_message_${lang.id}`]:
                                        groupTranslations[
                                            'widget_no_records_message'
                                        ],
                                    [`widget_more_label_${lang.id}`]:
                                        groupTranslations['widget_more_label'],
                                }
                            })

                            return {
                                ...prevState,
                                [`value_${lang.id}`]:
                                    groupTranslations['value'],
                                [`cta_label_${lang.id}`]:
                                    groupTranslations['cta_label'],
                                [`widget_header_${lang.id}`]:
                                    groupTranslations['widget_header'],
                                [`widget_sub_header_${lang.id}`]:
                                    groupTranslations['widget_sub_header'],
                                [`widget_no_records_message_${lang.id}`]:
                                    groupTranslations[
                                        'widget_no_records_message'
                                    ],
                                [`widget_more_label_${lang.id}`]:
                                    groupTranslations['widget_more_label'],
                            }
                        })

                        return true
                    })
                }

                setAvailablePages(response.data.data.available_pages || [])
                setAvailableWidgets(response.data.data.available_widgets || [])
                setAvailableModules(response.data.data.available_modules || [])

                if (isViewOrEdit) {
                    setAllowRemoveImage(
                        response.data.data.section.image_id !== null
                    )
                    setPreviewSrc(
                        response.data.data.section.image_id
                            ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.section.image_id}`
                            : ''
                    )
                } else {
                    setPreviewSrc('')
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                triggerToast({
                    severity: 'error',
                    summary: 'Not found',
                    detail: 'Record was not found.',
                })
                navigate('/content/page-sections')
            } else {
                setErrMsg('Failed to load page')
            }
        }
    }, [axiosPrivate, id, initData.languages.data, isViewOrEdit, triggerToast, navigate])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        record.remove_pillar_image =
            !record.pillar_image &&
            record.image_id !== null &&
            !allowRemoveImage

        try {
            const routePath = isViewOrEdit
                ? '/page-components/sections/' + id
                : '/page-components/sections/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            if (err?.response?.status === 404) {
                triggerToast({
                    severity: 'error',
                    summary: 'Not found',
                    detail: 'Record was not found.',
                })
                if (onClose) {
                    onClose()
                } else {
                    navigate('/content/page-sections')
                }
            } else {
                handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
            }
        }
    }

    const hideDialog = () => {
        setPageDialog(false)
        if (onClose) {
            onClose()
        } else {
            navigate('/content/page-sections')
        }
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/page-components/sections/delete/' + id}
                    record={pageSection}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete page section:
                                <strong> {pageSection.name}</strong>?
                            </p>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'pageSection-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={PageSectionFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="pageSection-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <RecordStatusField
                                        id="formActive"
                                        name="active"
                                        onLabel="De-Activate"
                                        offLabel="Activate"
                                        formikProps={formikProps}
                                    />
                                </Col>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col lg={4}>
                                                <TextField
                                                    id="formSectionInternalName"
                                                    name="name"
                                                    label="Section Internal Name"
                                                    placeholder="Enter page name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                            <Col lg={4}>
                                                <SelectField
                                                    id="formPage"
                                                    name="page_sections_id"
                                                    label="Page"
                                                    formikProps={formikProps}
                                                    choices={availablePages}
                                                    required
                                                />
                                            </Col>

                                            <Col lg={4}>
                                                <SelectField
                                                    id="formCTATarget"
                                                    name="cta_target"
                                                    label="CTA Target"
                                                    formikProps={formikProps}
                                                    choices={[
                                                        {
                                                            id: 'internal',
                                                            name: 'Internal',
                                                        },
                                                        {
                                                            id: 'external',
                                                            name: 'External',
                                                        },
                                                    ]}
                                                />
                                                <RenderTranslatedFields
                                                    id="pageSectionCTA"
                                                    formikProps={formikProps}
                                                    groupClassName={
                                                        formikProps.values
                                                            .cta_target ===
                                                            null ||
                                                        formikProps.values
                                                            .cta_target === ''
                                                            ? 'd-none'
                                                            : ''
                                                    }
                                                    fields={[
                                                        {
                                                            id: 'formButtonLabel',
                                                            type: 'text',
                                                            name: 'cta_label',
                                                            label: 'Button Label',
                                                            placeholder:
                                                                'Enter button label',
                                                        },
                                                    ]}
                                                />
                                                <SelectField
                                                    id="formTargetPage"
                                                    name="cta_page_sections_id"
                                                    label="Target Page"
                                                    formikProps={formikProps}
                                                    choices={availablePages}
                                                    groupClassName={
                                                        formikProps.values
                                                            .cta_target ===
                                                        'internal'
                                                            ? ''
                                                            : 'd-none'
                                                    }
                                                />

                                                <TextField
                                                    id="formUrl"
                                                    name="cta_url"
                                                    label="Url"
                                                    placeholder="Enter target URL"
                                                    formikProps={formikProps}
                                                    groupClassName={
                                                        formikProps.values
                                                            .cta_target ===
                                                        'external'
                                                            ? ''
                                                            : 'd-none'
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                                <Col lg={12}>
                                    <fieldset>
                                        <legend style={{ width: '100%' }}>
                                            <Row>
                                                <Col sm={6}>Content</Col>
                                                <Col sm={6}>
                                                    <Form.Check
                                                        type="switch"
                                                        reverse
                                                        id="content-is-widget-switch"
                                                        label="Use data type"
                                                        name="use_data_type"
                                                        value={
                                                            'contentIsWidget'
                                                        }
                                                        role="button"
                                                        checked={
                                                            contentIsWidget
                                                        }
                                                        onChange={(e) => {
                                                            setContentIsWidget(
                                                                !contentIsWidget
                                                            )
                                                            formikProps.setFieldValue(
                                                                'use_data_type',
                                                                e.target.checked
                                                            )
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </legend>
                                        {contentIsWidget ? (
                                            <PageSectionFormContent
                                                formikProps={formikProps}
                                                availableModules={
                                                    availableModules
                                                }
                                                availableWidgets={
                                                    availableWidgets
                                                }
                                                availablePages={availablePages}
                                            />
                                        ) : (
                                            <Row>
                                                <Col lg={9}>
                                                    <RenderTranslatedFields
                                                        id="pageSectionContent"
                                                        formikProps={
                                                            formikProps
                                                        }
                                                        fields={[
                                                            {
                                                                id: 'formContent',
                                                                type: 'textarea',
                                                                name: 'value',
                                                                label: 'Content',
                                                                showLabel:
                                                                    false,
                                                                placeholder:
                                                                    'Enter button label',
                                                                enableEditor:
                                                                    true,
                                                            },
                                                        ]}
                                                    />
                                                </Col>
                                                <Col lg={3}>
                                                    <fieldset>
                                                        <legend>
                                                            Image
                                                            <HintMessage
                                                                message={
                                                                    <ImageReccomendedDimentionsMessage />
                                                                }
                                                            />
                                                        </legend>

                                                        <ImageField
                                                            id="pageSectionPillarImageFile"
                                                            name="pillar_image"
                                                            label="Image"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            imageId={
                                                                pageSection.image_id
                                                            }
                                                            alt={`${pageSection.name} image`}
                                                            allowRemoveImage={
                                                                allowRemoveImage
                                                            }
                                                            setAllowRemoveImage={
                                                                setAllowRemoveImage
                                                            }
                                                            previewSrc={
                                                                previewSrc
                                                            }
                                                            setPreviewSrc={
                                                                setPreviewSrc
                                                            }
                                                            updateRecord={
                                                                setPageSection
                                                            }
                                                        />
                                                    </fieldset>
                                                </Col>
                                            </Row>
                                        )}
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="pageSection-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={pageDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '75vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : pageSection.name
                                : 'Create new page section'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : undefined}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : pageSection.name
                                : 'Create new page section'
                        }
                        buttons={[
                            {
                                variant: theme === 'dark' ? 'dark' : 'light',
                                label: 'Back to Page Sections',
                                icon: 'fa-solid fa-arrow-left',
                                url: '/content/page-sections',
                            },
                        ]}
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default PageSectionForm
